import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { api, ApiError } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";

const TEXT = {
  it: {
    kicker: "Architetti e studi",
    title: "Richiedi un account",
    intro:
      "L'account dà accesso al toolkit tecnico: modelli BIM/CAD, campioni materiali e capitolati pronti per il progetto. Le richieste sono approvate da VERSUS.",
    name: "Nome e cognome",
    studio: "Studio",
    vat: "Partita IVA",
    address: "Indirizzo dello studio",
    phone: "Telefono",
    submit: "Invia la richiesta",
    busy: "Invio…",
    sent: "Richiesta inviata.",
    sentBody:
      "Ti avviseremo via email appena lo studio è approvato. Non serve fare altro.",
    haveAccount: "Hai già un account? Accedi",
    errEmail: "Questa email è già registrata.",
    errGeneric: "Invio non riuscito. Controlla i dati e riprova.",
  },
  en: {
    kicker: "Architects & studios",
    title: "Request an account",
    intro:
      "The account unlocks the technical toolkit: BIM/CAD models, material samples and specifications ready for your project. Requests are approved by VERSUS.",
    name: "Full name",
    studio: "Studio",
    vat: "VAT number",
    address: "Studio address",
    phone: "Phone",
    submit: "Send request",
    busy: "Sending…",
    sent: "Request sent.",
    sentBody:
      "We'll email you as soon as the studio is approved. Nothing else to do.",
    haveAccount: "Already have an account? Sign in",
    errEmail: "That email is already registered.",
    errGeneric: "Could not send. Check the details and try again.",
  },
  es: {
    kicker: "Arquitectos y estudios",
    title: "Solicita una cuenta",
    intro:
      "La cuenta da acceso al toolkit técnico: modelos BIM/CAD, muestras de material y pliegos listos para tu proyecto. VERSUS aprueba las solicitudes.",
    name: "Nombre y apellidos",
    studio: "Estudio",
    vat: "NIF / VAT",
    address: "Dirección del estudio",
    phone: "Teléfono",
    submit: "Enviar solicitud",
    busy: "Enviando…",
    sent: "Solicitud enviada.",
    sentBody:
      "Te avisaremos por email en cuanto se apruebe el estudio. No hace falta nada más.",
    haveAccount: "¿Ya tienes cuenta? Entra",
    errEmail: "Ese email ya está registrado.",
    errGeneric: "No se pudo enviar. Revisa los datos e inténtalo de nuevo.",
  },
} as const;

export default function RegisterPage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    studioName: "",
    vatNumber: "",
    address: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const set = (k: keyof typeof form, v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.register({ ...form, locale });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409 ? tx.errEmail : tx.errGeneric,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-surface grid min-h-screen place-items-center px-5 py-10">
      <div className="glass-panel-strong w-full max-w-lg p-7">
        <div className="flex flex-col items-center text-center">
          <BrandMark className="h-14 w-14" />
          <div className="brand-kicker mt-3">{tx.kicker}</div>
          <h1 className="mt-1 font-serif text-xl text-paper">{tx.title}</h1>
          <p className="mt-2 max-w-md text-[0.76rem] leading-relaxed text-ice/70">
            {tx.intro}
          </p>
        </div>

        <hr className="hairline my-6" />

        {done ? (
          <div className="text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-green/50 bg-green/10">
              <Check className="h-5 w-5 text-green" />
            </div>
            <p className="mt-4 font-serif text-base text-paper">{tx.sent}</p>
            <p className="mt-2 text-[0.76rem] leading-relaxed text-ice/70">
              {tx.sentBody}
            </p>
            <Link
              to="/login"
              className="mt-5 inline-block text-[0.68rem] text-pink hover:underline"
            >
              {tx.haveAccount}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="field-label">{tx.name}</span>
              <input
                className="field"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </label>

            <label className="block">
              <span className="field-label">Email</span>
              <input
                className="field"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </label>

            <label className="block">
              <span className="field-label">Password</span>
              <input
                className="field"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
              />
            </label>

            <label className="block">
              <span className="field-label">{tx.studio}</span>
              <input
                className="field"
                value={form.studioName}
                onChange={(e) => set("studioName", e.target.value)}
              />
            </label>

            <label className="block">
              <span className="field-label">{tx.vat}</span>
              <input
                className="field"
                value={form.vatNumber}
                onChange={(e) => set("vatNumber", e.target.value)}
              />
            </label>

            <label className="block sm:col-span-2">
              <span className="field-label">{tx.address}</span>
              <input
                className="field"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
              />
            </label>

            <label className="block sm:col-span-2">
              <span className="field-label">{tx.phone}</span>
              <input
                className="field"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </label>

            {error && (
              <p className="text-xs text-pink sm:col-span-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="mt-2 rounded-full bg-magenta px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper transition hover:brightness-110 disabled:opacity-60 sm:col-span-2"
            >
              {busy ? tx.busy : tx.submit}
            </button>

            <Link
              to="/login"
              className="text-center text-[0.68rem] text-ice/60 hover:text-paper sm:col-span-2"
            >
              {tx.haveAccount}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
